/** Encoding a custom LLM provider's model into the new-session picker's
 *  flat ``<select>`` value space (FW-19 phase 2).
 *
 *  Built-in model slugs are opaque strings from each provider's own
 *  catalog; a custom provider's model slug is whatever the operator
 *  typed in when adding it (may contain "/" or ":", e.g. OpenRouter's
 *  ``meta-llama/llama-3.3-70b`` or Bedrock's ``anthropic.claude-v2:0``).
 *  To tell a custom pick apart from a built-in one without colliding
 *  with either catalog, every custom option's ``value`` carries the
 *  provider id alongside the slug under a reserved prefix.
 */
import type { TFunction } from "i18next";

import type { AvailableLlmProvider } from "@/api/llmProviders";

const PREFIX = "custom:";

/** The agent CLIs a custom model can drive (``AvailableLlmProvider.cli``,
 *  the backend's ``session_binding.cli_for_format``). The new-session
 *  form's agent picker and "can this user run sessions"
 *  (``useCanRunSessions``) both read this list. */
export const CUSTOM_MODEL_CLIS = ["codex", "claude_code"] as const;

export function customModelValue(providerId: string, slug: string): string {
  return `${PREFIX}${providerId}:${slug}`;
}

export function parseCustomModelValue(
  value: string,
): { providerId: string; slug: string } | null {
  if (!value.startsWith(PREFIX)) return null;
  const rest = value.slice(PREFIX.length);
  const at = rest.indexOf(":");
  if (at <= 0 || at === rest.length - 1) return null;
  return { providerId: rest.slice(0, at), slug: rest.slice(at + 1) };
}

export type CustomModelOption = {
  value: string;
  label: string;
  providerName: string;
  supportsTools: boolean | null;
  /** A member's own local model (FW-19 phase 3), run through their
   *  connector rather than a provider an admin added. */
  local: boolean;
  /** False only for a local model whose connector isn't attached right
   *  now: the picker still lists it, disabled, with an offline hint. */
  online: boolean;
  /** Why the picker lists this model disabled, or ``null`` when it can
   *  run a session. Mirrors the backend's create/send refusals
   *  (``session_binding.check_model_tools``): every session works through
   *  tool calls, so a model that can't call tools is never pickable, and
   *  a local one must have passed Test. A remote model that was never
   *  tested stays pickable (its admins see the Test warning). */
  blocked: CustomModelBlock | null;
};

export type CustomModelBlock = "offline" | "no_tools" | "untested";

export function customModelBlock(
  local: boolean,
  online: boolean,
  supportsTools: boolean | null,
): CustomModelBlock | null {
  if (!online) return "offline";
  if (supportsTools === false) return "no_tools";
  if (local && supportsTools === null) return "untested";
  return null;
}

/** The custom-model options the new-session picker offers for the
 *  selected agent CLI — only the providers whose ``cli`` matches it,
 *  since a provider's API format determines the one CLI that can
 *  actually drive it (``session_binding.cli_for_format``). */
export function customModelOptions(
  providers: readonly AvailableLlmProvider[],
  cli: string,
): CustomModelOption[] {
  return providers
    .filter((p) => p.cli === cli)
    .flatMap((p) => {
      const local = p.scope === "user";
      const online = p.online !== false;
      return p.models.map((m) => ({
        value: customModelValue(p.id, m.slug),
        label: m.display_name ?? m.slug,
        providerName: p.name,
        supportsTools: m.supports_tools,
        local,
        online,
        blocked: customModelBlock(local, online, m.supports_tools),
      }));
    });
}

/** Whether ``cli`` has a custom model that can run a session now: an
 *  offline local model, or one without tool calling, is listed but
 *  blocked. The new-session form and "can this user run sessions"
 *  (``useCanRunSessions``) both ask this. */
export function hasPickableCustomModel(
  providers: readonly AvailableLlmProvider[],
  cli: string,
): boolean {
  return customModelOptions(providers, cli).some((o) => o.blocked === null);
}

/** A blocked option's menu label: why it can't be picked, in brief. */
export function blockedLabel(t: TFunction, o: CustomModelOption): string {
  const vars = { model: o.label, provider: o.providerName };
  if (o.blocked === "offline") return t("session.model_custom_offline", vars);
  if (o.blocked === "untested") return t("session.model_custom_untested", vars);
  return t("session.model_custom_no_tools", vars);
}

/** A blocked option's tooltip: what to do about it. A local model is
 *  tested by its owner on the Account page; a remote one by an admin. */
export function blockedHint(t: TFunction, o: CustomModelOption): string {
  if (o.blocked === "offline") return t("session.model_custom_offline_hint");
  return o.local
    ? t("session.model_custom_needs_tools_local_hint")
    : t("session.model_custom_needs_tools_remote_hint");
}
